import wixWidget from 'wix-widget';
import { cart, product } from "wix-stores-frontend";
import WixEcomBackend from 'wix-ecom-backend';
import wixEcomFrontend from "wix-ecom-frontend";
import { formFactor } from 'wix-window-frontend';
import getSymbolFromCurrency from 'currency-symbol-map'
import _ from 'lodash';
import { debounce } from 'lodash';

import { trackAddToCart, getProductById } from 'backend/stores.web.js';
import { suffixCurrencies } from 'public/Utilities/misc';
import store from 'store';

// Debounce function to prevent multiple quick clicks
export const debouncedAddToCart = debounce((prodId) => {
    addProductToCart(prodId);
}, 200);

// Debounce function to prevent multiple quick clicks
export const debouncedQuickView = debounce((prodId) => {
    handleQuickView(prodId);
}, 200);

/**
 * Creates a debounced updater function for a specific element and property.
 * @param {string} elementId - The ID of the element to control.
 * @param {string} propName - The property name to update.
 * @param {number} [debounceDelay=500] - The debounce delay in milliseconds.
 * @param {number} [updateWait=1500] - The wait time after updating in milliseconds.
 * @returns {function} - A debounced function to update the element.
 */
export function createDebouncedUpdater(elementId, propName, debounceDelay = 500, updateWait = 1500, type = 'directWidgetProp') {
    let lastValue = 0;

    const updateValueDebounced = _.debounce(async () => {
        if (type == 'directWidgetProp') {
            await controllElementVisibility(elementId, 'disable');
            await setWidgetProp(propName, lastValue, 'direct');
            await new Promise(resolve => setTimeout(resolve, updateWait));
            await controllElementVisibility(elementId, 'enable');
        }
    }, debounceDelay);

    return (value) => {
        lastValue = value;
        updateValueDebounced();
    };
}

/**
 * @function
 * @description Updates the value of a Wix Widget property.
 * @param {string} propName - The name of the Wix Widget property.
 * @param {string | number | array | object} data - This can be event output or a direct value.
 * @param {string} updateType - Type of data receiving ('direct', 'event', 'checked'). Default is 'event'.
 * @returns nothing 
 */
export async function setWidgetProp(propName, data, updateType = 'event') {
    let propValue = null;

    switch (updateType) {
    case 'direct':
        propValue = data
        break;
    case 'event':
        propValue = data.target.value;
        break;
    case 'checked':
        propValue = data.target.checked;
        break;

    default:
        propValue = data
        break;
    }

    // Update the local store with the new property value.
    updateLocalStorePropKeyValue(propName, propValue);

    // Set the property value in the Wix Widget.
    await wixWidget.setProps({
        [propName]: propValue
    });
}

/**
 * @function
 * @description Updates a key-value pair in the local store under the 'props' namespace.
 * @param {string} propName - The name of the property (key) to be updated.
 * @param {any} propValue - The new value to be assigned to the property.
 * @returns {void}
 */
function updateLocalStorePropKeyValue(propName, propValue) {
    try {
        // Retrieve existing 'props' from the local store, or initialize an empty object if not present.
        let localProps = store.get('props') || {};

        // Update or add the specified key-value pair.
        localProps[propName] = propValue;

        // Save the updated 'props' object back to the local store.
        store.set('props', localProps);
    } catch (error) {
        // Handle and log any errors that may occur during the update process.
        console.error(`Error updating local store property: ${error.message}`);
    }
}

/**
 * @function
 * @description Controls the visibility and state of a Wix element.
 * @param {string} elementId - The ID of the Wix element.
 * @param {string} visibilityStatus - The desired visibility status ('show', 'hide', 'scroll', 'disable', 'enable').
 * @returns nothing 
 */
export async function controllElementVisibility(elementId, visibilityStatus) {
    try {
        if (Array.isArray(elementId)) {
            for (const id of elementId) {
                await applyVisibility(id, visibilityStatus);
            }
        } else {
            await applyVisibility(elementId, visibilityStatus);
        }
    } catch (error) {
        console.error("Error controlling element visibility:", error);
    }
}

export async function applyVisibility(elementId, visibilityStatus) {
    try {
        switch (visibilityStatus) {
        case 'show':
            await $w(elementId).show();
            await $w(elementId).expand();
            break;
        case 'hide':
            await $w(elementId).hide();
            await $w(elementId).collapse();
            break;
        case 'scroll':
            await $w(elementId).scrollTo();
            break;
        case 'disable':
            await $w(elementId).disable();
            break;
        case 'enable':
            await $w(elementId).enable();
            break;
        default:
            await $w(elementId).show();
            await $w(elementId).expand();
            break;
        }
    } catch (error) {
        console.error("Error applying visibility:", error);
    }
}

async function productGoingToAddToCart(prodId, prodQuantity = 1) {

    if (prodQuantity > 0) {
        if (!containsSlash(prodId)) {

            let prodObj = {
                "productId": prodId,
                "quantity": prodQuantity
            }
            return prodObj;

        } else {

            // variant ids
            if (containsDashAfterSlash(prodId)) {

                let { productId, variantId } = separateIds(prodId);

                const prodResult = await getProductById(productId);

                const variantResult = prodResult.variants.find(variant => {
                    return variant.variant_id === variantId;
                });

                if (prodResult && variantResult) {

                    let prodObj = {
                        "productId": productId,
                        "quantity": prodQuantity,
                        "options": {
                            "choices": variantResult.choices,
                            "variant_id": variantResult.variant_id
                        }
                    }
                    return prodObj;
                }

            }
            // Has options but no variant ids
            else {

                let variantCombination = await getNumberAfterSlash(prodId);

                if (variantCombination != null) {

                    if (variantCombination.length > 0) {

                        let variantIds = variantCombination.split("").map(Number);

                        let actualProductId = await getStringBeforeSlash(prodId);
                        const result = await getProductById(actualProductId);

                        const prodOptions = result.productOptions;

                        let choicesObj = {}

                        let i = 0;

                        for (let key in prodOptions) {

                            choicesObj[key] = prodOptions[key][variantIds[i]].description;
                            i++
                        }

                        let prodObj = {
                            "productId": result._id,
                            "quantity": prodQuantity,
                            "options": {
                                "choices": choicesObj
                            }
                        }

                        return prodObj
                    }
                }

            }

        }
    }
    return null;
}

export async function addProductToCart(prodId) {

    let instance = store.get('instance');

    try {

        // Pass the updated prodId and isVariant to productGoingToAddToCart
        let productGoingToAdd = await productGoingToAddToCart(prodId, 1);

        try {
            // Prepare lineItems array for addToCurrentCart
            const lineItems = [{
                catalogReference: {
                    appId: "215238eb-22a5-4c36-9e7b-e7c08025e04e",
                    catalogItemId: productGoingToAdd.productId,
                    options: productGoingToAdd.options?.variant_id ? { variantId: productGoingToAdd.options.variant_id } : productGoingToAdd.options?.choices ? { options: productGoingToAdd.options.choices } : undefined,
                },
                quantity: productGoingToAdd.quantity
            }];

            let cartBefore;
            try {
                cartBefore = await WixEcomBackend.currentCart.getCurrentCart();

            } catch (error) {
                cartBefore = await WixEcomBackend.cart.createCart({ lineItems });
            }

            // Track the add-to-cart action
            await trackAddToCart(cartBefore, instance.instance, productGoingToAdd);

            // Add products to the current cart
            await WixEcomBackend.currentCart.addToCurrentCart({ lineItems }).then((updatedCurrentCart) => {
                    wixEcomFrontend.refreshCart();
                    return updatedCurrentCart;
                })
                .catch((error) => {
                    console.error("Error on addProductToCart", error);
                });

            if (formFactor !== "Mobile") {
                cart.showMiniCart();
            }
        } catch (error) {
            console.error("Error while adding to cart or tracking:", error);
        }

    } catch (error) {
        console.error("Product not added to cart", error);
    }
}

async function handleQuickView(prodId) {

    await product
        .openQuickView(prodId)
        .catch((error) => {
            console.log(error);
        });
}

export async function getProductVariantsOptions(dropdownOptions, prodId) {
    // Filter the options to include only those that contain the given prodId
    const filteredOptions = dropdownOptions.filter(option => option.value.startsWith(prodId));

    // Return the filtered array of objects
    return filteredOptions;
}

export function formatPrice(currency, price) {
    // Get the currency symbol
    const symbol = getSymbolFromCurrency(currency);

    // Check if the currency is in the suffixCurrencies array
    if (suffixCurrencies.includes(currency)) {
        // If yes, format with the symbol as a suffix
        return `${price} ${symbol}`;
    } else {
        // Otherwise, format with the symbol as a prefix
        return `${symbol}${price}`;
    }
}

export function arraysEqual(arr1, arr2) {
    return arr1.length === arr2.length && arr1.every((val, idx) => val === arr2[idx]);
}

export async function formatDaysString(numberOfDays) {

    if (numberOfDays === 1) {
        return numberOfDays + " day";
    } else if (numberOfDays > 1) {
        return numberOfDays + " days";
    }
}

export function containsSlash(prodId) {
    return prodId.includes("/");
}

export function containsDashAfterSlash(prodId) {
    // Find the index of the first "/" character
    const slashIndex = prodId.indexOf("/");

    // If no "/" is found or "/" is the last character, return false
    if (slashIndex === -1) {
        return false;
    }

    // Check if there is a "-" character after the "/"
    const stringAfterSlash = prodId.substring(slashIndex + 1);
    return stringAfterSlash.includes("-");
}

export function separateIds(inputProductIdString) {
    // Split the input string by "/"
    let parts = inputProductIdString.split("/");

    // Extract productId and variantId
    let productId = parts[0];
    let variantId = parts.slice(1).join("/"); // Join the remaining parts

    return { productId, variantId };
}

export async function getNumberAfterSlash(prodVariant) {

    const index = prodVariant.indexOf("/");

    if (index !== -1) {
        const numberStr = prodVariant.slice(index + 1);
        return numberStr;
    }
    return null;
}

export async function getStringBeforeSlash(str) {

    const parts = str.split("/");

    return parts[0];
}